import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import moment from 'jalali-moment'
import posthog from 'posthog-js'
import { useCallback, useEffect, useState } from 'react'
import { FiChevronLeft } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'
import { getAnnounceList, getAnnounceTags } from 'services/announce'

const PAGE_SIZE = 5
const ALL_TAG: IAnnounceTag = { title: 'همه', key: '' }

/**
 * How long ago an announcement was sent, in days.
 *
 * `created_at` is already jalali («1405-05-01 16:33:05»), so it has to be
 * parsed as such — read as gregorian it lands six centuries in the future.
 * Null for anything unparseable rather than a wrong number.
 */
const ageInDays = (createdAt: string | undefined): number | null => {
    if (!createdAt) return null
    const parsed = moment(createdAt, 'jYYYY-jMM-jDD HH:mm:ss')
    return parsed.isValid() ? moment().diff(parsed, 'days') : null
}

function AnnounceCard({
    item,
    onDetail,
}: {
    item: IAnnounceItem
    onDetail: () => void
}) {
    const channels = item.channels?.map((c) => c.title).join('، ') || '—'
    return (
        <div className="mb-3 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-[0_2px_10px_rgba(0,0,0,0.05)]">
            <div className="mb-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">عنوان:</span>
                <span className="rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1">
                    {item.tags}
                </span>
            </div>
            <div className="mb-2 flex items-center justify-between text-sm text-gray-500">
                <span>ارسال از طریق: {channels}</span>
                <span dir="ltr">{item.created_at?.split(' ')[0]}</span>
            </div>
            <div className="mb-3 text-sm leading-relaxed text-text-body">
                <span className="text-gray-500">متن پیام: </span>
                {item.text}
            </div>
            <button
                onClick={onDetail}
                className="flex items-center gap-1 text-sm text-green-1 active:scale-95"
            >
                جزئیات
                <FiChevronLeft className="h-4 w-4" />
            </button>
        </div>
    )
}

/**
 * The old flat list of SMS announcements, unchanged, at its own route.
 *
 * The new log (`services/messagingHistory`) only holds what was sent after the
 * backend that keeps it went live, so this screen stays reachable for the
 * history behind that line — and is what the new page renders instead of
 * itself when that backend is not deployed yet.
 */
export default function AnnounceArchive() {
    const { id } = useParams()
    const navigate = useNavigate()
    const [tags, setTags] = useState<IAnnounceTag[]>([ALL_TAG])
    const [selectedKey, setSelectedKey] = useState<string>('')
    const [items, setItems] = useState<IAnnounceItem[]>([])
    const [page, setPage] = useState<number>(1)
    const [hasMore, setHasMore] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [loadingMore, setLoadingMore] = useState<boolean>(false)

    // category chips (once). "همه" is always first (empty key = no filter).
    useEffect(() => {
        if (!id) return
        getAnnounceTags(id)
            .then((res) => setTags([ALL_TAG, ...(res || [])]))
            .catch(() => setTags([ALL_TAG]))
    }, [id])

    const loadPage = useCallback(
        async (key: string, pageNumber: number, reset: boolean) => {
            if (!id) return
            reset ? setLoading(true) : setLoadingMore(true)
            try {
                const res = await getAnnounceList(
                    id,
                    pageNumber,
                    key || undefined
                )
                const batch = res?.Announcements || []
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasMore(batch.length >= PAGE_SIZE)
                // Reported from here rather than from the effect that triggers
                // the load: «viewed» is about the list a manager actually got,
                // and its size is only known once the answer lands. Page one
                // only — paging further is the same view, scrolled.
                if (reset) {
                    posthog.capture('announce_list_viewed', {
                        moneypool_id: Number(id),
                        tag_key: key,
                        item_count: batch.length,
                        has_more: batch.length >= PAGE_SIZE,
                    })
                }
            } catch (error) {
                if (reset) setItems([])
                setHasMore(false)
                // «در این دسته اطلاع‌رسانی‌ای ثبت نشده است» is what a failed
                // load renders, so an outage on this screen was indexed as an
                // empty history — of the one feature the fund is billed for.
                posthog.capture('announce_list_load_failed', {
                    moneypool_id: Number(id),
                    tag_key: key,
                    page: pageNumber,
                    status:
                        (error as { response?: { status?: number } })?.response
                            ?.status ?? null,
                })
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id]
    )

    // (re)load page 1 whenever the selected category changes
    useEffect(() => {
        setPage(1)
        loadPage(selectedKey, 1, true)
    }, [selectedKey, loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(selectedKey, next, false)
    }

    return (
        <div className="flex h-full flex-col bg-white">
            <Header title="اطلاع‌رسانی به اعضا" />

            {/* category chips */}
            <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-[#EFEFEF] px-3 py-3">
                {tags.map((t) => {
                    const active = t.key === selectedKey
                    return (
                        <button
                            key={t.key || 'all'}
                            onClick={() => {
                                // Only a real change: re-tapping the active
                                // chip reloads nothing and is not a filter.
                                if (t.key !== selectedKey) {
                                    posthog.capture(
                                        'announce_tag_filter_changed',
                                        {
                                            moneypool_id: Number(id),
                                            tag_key: t.key,
                                        }
                                    )
                                }
                                setSelectedKey(t.key)
                            }}
                            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                                active
                                    ? 'bg-green-1 text-white'
                                    : 'bg-main-bg text-text-body'
                            }`}
                        >
                            {t.title}
                        </button>
                    )
                })}
            </div>

            {/* list */}
            <div className="grow overflow-y-auto px-3 py-3">
                {loading ? (
                    <DotsLoading />
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-24 text-center text-gray-400">
                        <p>در این دسته اطلاع‌رسانی‌ای ثبت نشده است</p>
                    </div>
                ) : (
                    <>
                        {items.map((item) => (
                            <AnnounceCard
                                key={item.announce_request_id}
                                item={item}
                                onDetail={() => {
                                    // Everything here is already on the wire.
                                    // `item.text` is the announcement a manager
                                    // wrote to their members and never travels
                                    // with it; the costs are what make this the
                                    // analytics counterpart of the SMS bill.
                                    posthog.capture('announce_detail_opened', {
                                        moneypool_id: Number(id),
                                        tag_key: selectedKey,
                                        channels:
                                            item.channels?.map((c) => c.key) ??
                                            [],
                                        receivers_count: item.receivers_count,
                                        total_sms_cost_rial:
                                            item.total_sms_cost,
                                        total_bale_cost_rial:
                                            item.total_bale_cost,
                                        age_days: ageInDays(item.created_at),
                                    })
                                    navigate(
                                        `/fund/announce/${id}/detail/${item.announce_request_id}`
                                    )
                                }}
                            />
                        ))}
                        {hasMore && (
                            <button
                                onClick={onLoadMore}
                                disabled={loadingMore}
                                className="my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                            >
                                {loadingMore
                                    ? 'در حال بارگذاری...'
                                    : 'موارد بیشتر'}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
